import { useEffect, useState, type CSSProperties } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { hasRole, COUNCIL_REVIEWER_IDS } from '@death-star/shared';
import { useApiQuery } from '../use-api.js';
import { useAuth } from '../auth.js';
import { useRole } from '../use-role.js';
import { apiFetch, UnauthorizedError } from '../api-client.js';
import {
  validateSuppressionForm,
  buildCreateBody,
  isSafeUrl,
  type SuppressionFormState,
} from './suppression-form-helpers.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { ORGS } from '../orgs.js';

interface Suppression {
  pk: string;        // ORG#<org>
  sk: string;        // SUP#<id>
  org: string;
  reviewer: string;
  tokens: string[];
  rationale: string;
  expires_at_epoch: number;
  created_at: string;
  source: 'digest_dismiss' | 'manual';
  source_ref?: { week: string; cluster_id: string; em_email: string } | string;
}

const EMPTY_FORM: SuppressionFormState = {
  org: '',
  reviewer: '',
  match_kind: 'token_overlap',
  tokensInput: '',
  min_overlap: '',
  rationale: '',
  source_ref: '',
  expires_at: '',
};

const emptyStateBox: CSSProperties = {
  background: 'var(--loop-neutral-100)',
  border: '1px solid var(--loop-neutral-200)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-300)',
  textAlign: 'center',
};

const extendBtnStyle: CSSProperties = {
  background: 'var(--loop-brand-600)',
  color: 'var(--loop-neutral-0)',
  border: 'none',
  borderRadius: 'var(--loop-radius-sm)',
};

const revokeBtnStyle: CSSProperties = {
  background: 'var(--loop-red-600)',
  color: 'var(--loop-neutral-0)',
  border: 'none',
  borderRadius: 'var(--loop-radius-sm)',
};

const cardStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  border: '1px solid var(--loop-neutral-200)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-300)',
  marginBottom: 24,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 528,
  color: 'var(--loop-neutral-900)',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 10px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-xs)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
};

const errorStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--loop-red-600)',
  marginTop: 2,
};

const hintStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--loop-neutral-600)',
  marginTop: 2,
};

const fieldStyle: CSSProperties = {
  marginBottom: 16,
};

function fmtEpoch(epoch: number | null): string {
  if (epoch === null) return 'Never';
  return new Date(epoch * 1000).toLocaleDateString();
}

function CreateForm({ onCreated }: { onCreated: () => void }) {
  const { idToken, refreshSession } = useAuth();
  const [form, setForm] = useState<SuppressionFormState>(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validation = validateSuppressionForm(form);
  const errors = validation.ok ? {} : validation.errors;

  const mutation = useMutation({
    mutationFn: async () => {
      const body = buildCreateBody(form);
      const res = await apiFetch('/api/suppressions', idToken!, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
      }
      return res.json();
    },
    onSuccess: () => {
      setForm(EMPTY_FORM);
      setShowForm(false);
      setSubmitError(null);
      setSuccessMsg('Suppression created.');
      onCreated();
      setTimeout(() => setSuccessMsg(null), 4000);
    },
    onError: (err) => {
      setSubmitError((err as Error).message);
    },
  });

  useEffect(() => {
    if (mutation.error instanceof UnauthorizedError) void refreshSession();
  }, [mutation.error, refreshSession]);

  if (!showForm) {
    return (
      <div style={{ marginBottom: 24 }}>
        {successMsg && (
          <p style={{ color: 'var(--loop-green-700)', fontSize: '0.875rem', marginBottom: 8 }}>{successMsg}</p>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            className="btn-sm"
            style={{ background: 'var(--loop-brand-600)', color: 'var(--loop-neutral-0)', border: 'none', borderRadius: 'var(--loop-radius-sm)', cursor: 'pointer' }}
            onClick={() => setShowForm(true)}
          >
            + Add suppression
          </button>
          <span
            style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', fontStyle: 'italic' }}
            title="Most suppressions are added automatically when you 👎 a finding in the weekly digest. Use this form for proactive suppressions before findings appear."
          >
            ⓘ Manual creation is the exception — most suppressions come from 👎 reactions on weekly digest findings.
          </span>
        </div>
      </div>
    );
  }

  const setField = <K extends keyof SuppressionFormState>(key: K, value: SuppressionFormState[K]) => {
    setForm(f => ({ ...f, [key]: value }));
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--loop-neutral-1100)', margin: 0 }}>
          Add suppression
        </h3>
        <button
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--loop-neutral-600)', fontSize: '1.25rem', lineHeight: 1, padding: 0 }}
          onClick={() => { setShowForm(false); setSubmitError(null); }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        <div style={fieldStyle}>
          <label style={labelStyle}>Organisation *</label>
          <select
            style={inputStyle}
            value={form.org}
            onChange={e => setField('org', e.target.value)}
          >
            <option value="">— select —</option>
            {ORGS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          {errors['org'] && <p style={errorStyle}>{errors['org']}</p>}
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Reviewer *</label>
          <select
            style={inputStyle}
            value={form.reviewer}
            onChange={e => setField('reviewer', e.target.value)}
          >
            <option value="">— select —</option>
            {COUNCIL_REVIEWER_IDS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {errors['reviewer'] && <p style={errorStyle}>{errors['reviewer']}</p>}
        </div>

        <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Tokens * (comma-separated)</label>
          <input
            type="text"
            style={inputStyle}
            value={form.tokensInput}
            onChange={e => setField('tokensInput', e.target.value)}
            placeholder="e.g.: unused, variable, fixture"
          />
          {/* One token is accepted — the API schema is
              z.array(z.string().min(1)).min(1).max(50) — but it matches very
              broadly, so "prefer two or more" is guidance in the hint rather
              than a rule. The placeholder previously claimed "at least 2
              tokens", which nothing enforced on either side. */}
          {errors['tokensInput']
            ? <p style={errorStyle}>{errors['tokensInput']}</p>
            : <p style={hintStyle}>Comma-separated tokens from a finding. A suppression fires when ≥ min_overlap tokens match, so a single token matches broadly — two or more is usually what you want.</p>
          }
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>min_overlap</label>
          <input
            type="number"
            style={inputStyle}
            value={form.min_overlap}
            onChange={e => setField('min_overlap', e.target.value)}
            placeholder="Defaults to all tokens if blank"
            min={1}
          />
          {errors['min_overlap']
            ? <p style={errorStyle}>{errors['min_overlap']}</p>
            : <p style={hintStyle}>Defaults to all tokens if blank.</p>
          }
        </div>

        <div style={fieldStyle}>
          <label style={labelStyle}>Expires at</label>
          <input
            type="date"
            style={inputStyle}
            value={form.expires_at}
            onChange={e => setField('expires_at', e.target.value)}
          />
          {errors['expires_at']
            ? <p style={errorStyle}>{errors['expires_at']}</p>
            : <p style={hintStyle}>Defaults to 90 days from now if blank.</p>
          }
        </div>

        <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Example finding URL</label>
          <input
            type="text"
            style={inputStyle}
            value={form.source_ref}
            onChange={e => setField('source_ref', e.target.value)}
            placeholder="e.g. https://github.com/rewindio/foo/pull/99"
          />
          <p style={hintStyle}>Optional — link to a representative finding for context.</p>
        </div>

        <div style={{ ...fieldStyle, gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Rationale * (min 10 chars)</label>
          <textarea
            style={{ ...inputStyle, minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }}
            value={form.rationale}
            onChange={e => setField('rationale', e.target.value)}
            placeholder="Why is this pattern safe to suppress?"
          />
          {errors['rationale'] && <p style={errorStyle}>{errors['rationale']}</p>}
        </div>
      </div>

      {submitError && (
        <p style={{ ...errorStyle, marginBottom: 12 }}>Submit failed: {submitError}</p>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          className="btn-sm"
          style={{ background: 'none', border: '1px solid var(--loop-neutral-300)', borderRadius: 'var(--loop-radius-sm)', cursor: 'pointer', color: 'var(--loop-neutral-700)' }}
          onClick={() => { setShowForm(false); setSubmitError(null); }}
          disabled={mutation.isPending}
        >
          Cancel
        </button>
        <button
          className="btn-sm"
          style={{
            background: validation.ok ? 'var(--loop-brand-600)' : 'var(--loop-neutral-300)',
            color: 'var(--loop-neutral-0)',
            border: 'none',
            borderRadius: 'var(--loop-radius-sm)',
            cursor: validation.ok && !mutation.isPending ? 'pointer' : 'not-allowed',
            opacity: mutation.isPending ? 0.6 : 1,
          }}
          onClick={() => { if (validation.ok) mutation.mutate(); }}
          disabled={!validation.ok || mutation.isPending}
        >
          {mutation.isPending ? 'Creating…' : 'Create suppression'}
        </button>
      </div>
    </div>
  );
}

export function Suppressions() {
  const { idToken, refreshSession } = useAuth();
  const qc = useQueryClient();
  const { role, loading: roleLoading } = useRole();
  const canWrite = !roleLoading && hasRole(role, 'triager');

  const { data, isLoading, error } = useApiQuery<Suppression[]>(
    ['suppressions'],
    '/api/suppressions'
  );

  const extendMutation = useMutation({
    mutationFn: async (sup: Suppression) => {
      const rowId = `${sup.pk}|${sup.sk}`;
      const res = await apiFetch(`/api/suppressions/${encodeURIComponent(rowId)}/extend`, idToken!, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ days: 30, rationale: 'extended via portal' }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
  });
  useEffect(() => {
    if (extendMutation.error instanceof UnauthorizedError) void refreshSession();
  }, [extendMutation.error, refreshSession]);

  const revokeMutation = useMutation({
    mutationFn: async (sup: Suppression) => {
      const rowId = `${sup.pk}|${sup.sk}`;
      const res = await apiFetch(`/api/suppressions/${encodeURIComponent(rowId)}`, idToken!, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['suppressions'] }),
  });
  useEffect(() => {
    if (revokeMutation.error instanceof UnauthorizedError) void refreshSession();
  }, [revokeMutation.error, refreshSession]);

  const sorted = [...(data ?? [])].sort((a, b) => {
    const ae = a.expires_at_epoch ?? Infinity;
    const be = b.expires_at_epoch ?? Infinity;
    return ae - be;
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>Suppressions</h2>
        <ConceptHelpIcon slug="suppression" />
      </div>

      {canWrite && <CreateForm onCreated={() => qc.invalidateQueries({ queryKey: ['suppressions'] })} />}

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {String(error)}</p>}

      {data && sorted.length === 0 && (
        <div style={emptyStateBox}>
          <p style={{ fontWeight: 600 }}>No active suppressions.</p>
          <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
            Suppressions are created when an EM/TL dismisses a clustered 👎 pattern in
            the weekly digest, or via the form above. Each suppression silences
            (reviewer, tokens) across the org for 90 days. Once active, suppressions are
            listed here with Extend and Revoke controls.
          </p>
        </div>
      )}

      {data && sorted.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
            <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
              <tr>
                {['Reviewer', 'Tokens', 'Source', 'Rationale', 'Expires', ''].map(h => (
                  <th key={h} style={{ padding: '8px 16px', color: 'var(--loop-neutral-700)', fontWeight: 528 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => (
                <tr key={`${s.pk}|${s.sk}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                  <td style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.reviewer}</td>
                  <td
                    style={{ padding: '8px 16px', fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={s.tokens.join(' ')}
                  >
                    {s.tokens.slice(0, 6).join(' ')}{s.tokens.length > 6 ? ' …' : ''}
                  </td>
                  <td style={{ padding: '8px 16px', fontSize: '0.75rem' }}>
                    {s.source === 'digest_dismiss' && s.source_ref && typeof s.source_ref === 'object' ? (
                      <a href={`/weekly-digest?week=${s.source_ref.week}`} style={{ color: 'var(--loop-blue-600)' }}>
                        {s.source_ref.week} · {s.source_ref.cluster_id.slice(0, 20)}…
                      </a>
                    ) : s.source === 'manual' && typeof s.source_ref === 'string' && isSafeUrl(s.source_ref) ? (
                      <a href={s.source_ref} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--loop-blue-600)' }}>
                        Manual
                      </a>
                    ) : s.source === 'manual' && typeof s.source_ref === 'string' && s.source_ref ? (
                      <span style={{ color: 'var(--loop-neutral-700)' }}>{s.source_ref}</span>
                    ) : (
                      <span style={{ color: 'var(--loop-neutral-700)' }}>Manual</span>
                    )}
                  </td>
                  <td
                    style={{ padding: '8px 16px', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--loop-neutral-700)' }}
                    title={s.rationale}
                  >
                    {s.rationale || '—'}
                  </td>
                  <td style={{ padding: '8px 16px' }}>{fmtEpoch(s.expires_at_epoch)}</td>
                  <td style={{ padding: '8px 16px' }}>
                    {canWrite && (
                      <div style={{ display: 'inline-flex', gap: 'var(--loop-space-150)', flexWrap: 'wrap' }}>
                        <button
                          className="btn-sm"
                          style={{ ...extendBtnStyle, opacity: extendMutation.isPending ? 0.5 : 1, cursor: extendMutation.isPending ? 'not-allowed' : 'pointer' }}
                          onClick={() => extendMutation.mutate(s)}
                          disabled={extendMutation.isPending}
                        >
                          Extend 30d
                        </button>
                        <button
                          className="btn-sm"
                          style={{ ...revokeBtnStyle, opacity: revokeMutation.isPending ? 0.5 : 1, cursor: revokeMutation.isPending ? 'not-allowed' : 'pointer' }}
                          onClick={() => {
                            if (window.confirm('Revoke this suppression? Future PRs from this reviewer matching these tokens will start flagging again.')) {
                              revokeMutation.mutate(s);
                            }
                          }}
                          disabled={revokeMutation.isPending}
                        >
                          Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
